// src/pages/SupplyChain.jsx
//
// Companies-monitored list, driven end-to-end by
// /supply_chain/list_tracked_suppliers.php. Search, sort, and pagination
// all round-trip to the server. We write the
// returned snapshot back to localStorage so SupplyChainDetail can still
// pull vendor metadata (name / industry / size / country) for its header
// without firing a second request.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search as SearchIcon,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  AlertTriangle,
  Globe,
  Network,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";
import { useSupplierSecurityAccess } from "../utils/brandProtection";
import { FLAG, saveCompanies } from "../data/supplyChainCompanies";

/* ────────────────────────── Config ────────────────────────── */

const LIST_TRACKED_SUPPLIERS_URL =
  "https://whiteintel.io/api/regular/app/v2/supply_chain/list_tracked_suppliers.php";

const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

const primaryBtn =
  "inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 transition-colors";

const ghostBtn =
  "inline-flex items-center gap-1.5 rounded-md border border-sky-900/30 bg-transparent hover:bg-sky-900/30 px-2.5 py-1.5 text-[12px] font-medium text-neutral-300 hover:text-sky-300 transition-colors";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

/* ────────────────────────── Tone & format helpers ────────────────────────── */

function gradeTone(grade) {
  const g = String(grade || "").toUpperCase();
  if (g === "F" || g === "D")
    return { text: "text-rose-300", border: "border-rose-500/40", bg: "bg-rose-500/10" };
  if (g === "C")
    return { text: "text-amber-300", border: "border-amber-500/40", bg: "bg-amber-500/10" };
  if (g === "B")
    return { text: "text-yellow-300", border: "border-yellow-500/40", bg: "bg-yellow-500/10" };
  if (g === "A")
    return { text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/10" };
  return { text: "text-neutral-300", border: "border-neutral-700/40", bg: "bg-neutral-700/10" };
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";
  if (s === "paused") return "border-amber-500/40 bg-amber-500/10 text-amber-200";
  if (s === "archived") return "border-neutral-700 bg-neutral-700/10 text-neutral-400";
  return "border-neutral-700 bg-neutral-700/10 text-neutral-300";
}

// Backend can return 0 for a vendor that has no recent dark-web signal at all.
// Reading "0" makes analysts think we didn't measure anything, so we surface a
// small non-zero baseline (5) instead. Only applied to the composite risk
// score — delta + per-source indices keep their literal value.
function displayRiskScore(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  if (num === 0) return "5";
  return fmtScore(num);
}

function fmtScore(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: num % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 2,
  });
}

// Relative time for "last event / last scored" cells. MySQL DATETIME comes
// back as "YYYY-MM-DD HH:MM:SS" with no timezone; we treat it as local. The
// goal is "feel for freshness", not surgical precision.
function relTime(s) {
  if (!s) return "—";
  // Convert "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SS" so Date parses
  // consistently across browsers.
  const iso = String(s).includes("T") ? s : String(s).replace(" ", "T");
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return s;
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return iso.slice(0, 10);
  const sec = diffMs / 1000;
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 30) return `${Math.floor(sec / 86400)}d ago`;
  return iso.slice(0, 10);
}

/* ────────────────────────── Tiny presentation components ────────────────────────── */

// Mirrors Dashboard.jsx — Google's S2 favicon service with a Globe fallback.
function DomainFavicon({ domain, size = 16, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!domain || failed) {
    return (
      <Globe
        aria-hidden
        className={`shrink-0 text-neutral-500 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size * 2}`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={`shrink-0 rounded-sm ${className}`}
    />
  );
}

function GradePill({ grade }) {
  const t = gradeTone(grade);
  return (
    <span
      className={`inline-flex items-center justify-center rounded border px-1.5 py-0 text-[11px] font-bold ${t.border} ${t.bg} ${t.text}`}
    >
      {grade || "—"}
    </span>
  );
}

function StatusChip({ status }) {
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10.5px] capitalize ${statusTone(status)}`}
    >
      {status}
    </span>
  );
}

/* ────────────────────────── Upgrade-required gate ──────────────────────────
 *
 * Mirrors the Brand Protection / Payment Fraud gate: centered frosted card,
 * round sky icon chip, two CTAs (View plans + Back to dashboard). Rendered
 * when /brand_protection_check.php reports supplier_available === false.
 */

function UpgradeRequiredCta() {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto mt-12 px-4">
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)] p-8 text-center">
        <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
          <Network className="h-6 w-6 text-sky-300" />
        </div>
        <h2 className="text-xl font-semibold text-white">
          Supplier Security is a premium module
        </h2>
        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
          Continuous credential-exposure scoring for your vendors and partners.
          Available on{" "}
          <span className="text-sky-200 font-medium">Enterprise</span>{" "}
          (64 suppliers),{" "}
          <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
          (128), and{" "}
          <span className="text-sky-200 font-medium">Extended Threat Intelligence</span>{" "}
          (256).
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/upgrades")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
          >
            View plans
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── Page ────────────────────────── */

export default function SupplyChain() {
  const navigate = useNavigate();
  const supplierAccess = useSupplierSecurityAccess();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Filter / sort state. Everything below round-trips to the API; the table
  // never re-sorts client-side. Default sort matches the backend default
  // (worst risk first).
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("score");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [data, setData] = useState({ suppliers: [], total: 0, limit: 10, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Server-side gate: any supplier endpoint that replies with
  // "You must belong to an organization …" flips this on. We OR it into the
  // entitlement gate so both reasons land on the same upgrade CTA.
  const [orgGate, setOrgGate] = useState(false);
  const gateActive =
    (!supplierAccess.loading && supplierAccess.available === false) || orgGate;

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever a filter that affects the result set changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sort, order, pageSize]);

  // We use a ref to track the latest request so a slow earlier response
  // can't clobber the page state with stale data.
  const reqIdRef = useRef(0);

  useEffect(() => {
    // Don't fire the list call when the user is gated — we'd just get an
    // error response back, and we're not going to render the table anyway.
    if (gateActive) return;
    const reqId = ++reqIdRef.current;
    let aborted = false;
    setLoading(true);
    setErr(null);

    const offset = (page - 1) * pageSize;
    const body = {
      search: debouncedSearch || undefined,
      sort,
      order,
      limit: pageSize,
      offset,
    };

    fetch(LIST_TRACKED_SUPPLIERS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        if (res.status === 401) {
          redirectToLogin();
          return null;
        }
        const text = await res.text();
        let payload = null;
        try {
          payload = text ? JSON.parse(text) : null;
        } catch {
          payload = null;
        }
        if (!res.ok) {
          const msg = payload?.error || `HTTP ${res.status}`;
          // The org-gate trip-wire: only the supplier-side endpoints surface
          // this exact phrase, so a regex check is enough — no need to plumb
          // a dedicated error code.
          if (/must belong to an organization/i.test(msg)) {
            setOrgGate(true);
            return null;
          }
          throw new Error(msg);
        }
        return payload;
      })
      .then((payload) => {
        if (aborted || reqId !== reqIdRef.current || !payload) return;
        setData({
          suppliers: payload.suppliers || [],
          total: payload.total ?? 0,
          limit: payload.limit ?? pageSize,
          offset: payload.offset ?? offset,
        });
        // Write-through cache so SupplyChainDetail can still read vendor
        // metadata from localStorage without an extra request.
        try {
          const cacheRows = (payload.suppliers || []).map((s) => ({
            domain: s.domain,
            name: s.display_name || s.domain,
            sighting: "—",
            identity: s.last_score !== null ? Math.round(Number(s.last_score)) : 0,
            malware: { v: 0, d: "" },
            breach: { v: 0, d: "" },
            phish: { v: 0, d: "" },
            combo: { v: 0, d: "" },
            size: s.size || "",
            country: s.country || "",
            industry: s.industry || "",
            supplier_id: s.id,
            added: (s.created_at || "").slice(0, 10),
          }));
          if (cacheRows.length > 0) saveCompanies(cacheRows);
        } catch {
          // localStorage failures are non-fatal for the UI.
        }
      })
      .catch((e) => {
        if (aborted || reqId !== reqIdRef.current) return;
        setErr(e?.message || "Could not load suppliers.");
      })
      .finally(() => {
        if (aborted || reqId !== reqIdRef.current) return;
        setLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [debouncedSearch, sort, order, page, pageSize, gateActive]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  // Show no more than 7 page buttons at a time (current + 3 on each side).
  const pageWindow = useMemo(() => {
    const span = 3;
    const lo = Math.max(1, page - span);
    const hi = Math.min(totalPages, page + span);
    const out = [];
    for (let p = lo; p <= hi; p++) out.push(p);
    return out;
  }, [page, totalPages]);

  const onSort = (key) => {
    if (key === sort) {
      setOrder((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setOrder("desc");
    }
  };

  const SortHeader = ({ k, children, className = "" }) => {
    const active = sort === k;
    return (
      <th
        scope="col"
        className={`px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400 select-none cursor-pointer hover:text-neutral-200 ${className}`}
        onClick={() => onSort(k)}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active &&
            (order === "asc" ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            ))}
        </span>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Supplier Security
            </h1>
          </div>
          <button
            type="button"
            onClick={() => navigate("/supplier-security/manage")}
            className={primaryBtn}
          >
            Manage Suppliers
          </button>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="supplychain"
          onChange={() => {}}
        />

        <main className="relative flex-1 px-4 md:px-6 pb-10">
          {gateActive ? (
            <UpgradeRequiredCta />
          ) : (
          <section className={`${panel} mt-2`}>
            {/* Card header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-sky-900/30 px-4 py-3">
              <div className="text-[14px] font-semibold text-neutral-100">
                Suppliers{" "}
                <span className="text-neutral-500 font-normal">
                  ({data.total.toLocaleString()})
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {loading && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-sky-300">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading
                  </span>
                )}
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search domain or name"
                    className="rounded-md border border-sky-900/30 bg-[#0b1426] pl-8 pr-3 py-1.5 text-[12px] text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-sky-500/50 transition-colors w-full sm:w-56"
                  />
                </div>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md border border-sky-900/30 bg-transparent hover:bg-sky-900/30 p-1.5 text-neutral-400 hover:text-sky-300 transition-colors"
                  aria-label="Export"
                  title="Export coming soon"
                  disabled
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Mobile card list — used below md so the 8-column desktop
                table doesn't get squeezed into a phone. Each card is a
                tappable equivalent of a row and surfaces the same fields,
                just stacked. */}
            <div className="md:hidden">
              {err && (
                <div className="px-4 py-10 text-center text-[13px] text-rose-300">
                  <div className="inline-flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    {err}
                  </div>
                </div>
              )}

              {!err && data.suppliers.length === 0 && !loading && (
                <div className="px-4 py-10 text-center text-[13px] text-neutral-500">
                  {debouncedSearch
                    ? "No suppliers match your search."
                    : "No suppliers monitored yet. Add one from Manage Suppliers."}
                </div>
              )}

              {!err && data.suppliers.map((r) => {
                const delta = Number(r.last_score_delta || 0);
                return (
                  <button
                    key={r.id ?? r.domain}
                    type="button"
                    onClick={() => navigate(`/supplier-security/${encodeURIComponent(r.domain)}`)}
                    className="w-full text-left border-t border-sky-900/20 px-4 py-3 hover:bg-sky-900/15 active:bg-sky-900/25 transition-colors"
                  >
                    {/* Identity + grade */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-8 w-8 shrink-0 rounded-md border border-sky-900/40 bg-[#0b1426] flex items-center justify-center overflow-hidden">
                          <DomainFavicon domain={r.domain} size={18} />
                        </div>
                        <div className="leading-tight min-w-0">
                          <div className="text-[14px] font-medium text-sky-300 truncate">
                            {r.display_name || r.domain}
                          </div>
                          <div className="text-[11.5px] text-neutral-500 truncate">{r.domain}</div>
                        </div>
                      </div>
                      <div className="shrink-0">
                        <GradePill grade={r.last_grade} />
                      </div>
                    </div>

                    {/* Stat strip — Score / Industry / Country */}
                    <div className="mt-3 grid grid-cols-3 gap-2 text-[11.5px]">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Risk Score</div>
                        <div className="flex items-center gap-1 text-neutral-100 font-medium tabular-nums">
                          {displayRiskScore(r.last_score)}
                          {delta > 0 && <ArrowUpRight className="h-3 w-3 text-rose-400" />}
                          {delta < 0 && <ArrowDownRight className="h-3 w-3 text-emerald-400" />}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Industry</div>
                        <div className="text-neutral-300 truncate">{r.industry || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Country</div>
                        {r.country ? (
                          <div className="text-neutral-300">
                            <span className="text-[14px] leading-none mr-1">{FLAG[r.country] || ""}</span>
                            {r.country}
                          </div>
                        ) : (
                          <div className="text-neutral-600">—</div>
                        )}
                      </div>
                    </div>

                    {/* Footer — Last event line + Status / Last scored */}
                    <div className="mt-3 pt-2 border-t border-sky-900/20 flex items-end justify-between gap-3">
                      <div className="min-w-0 leading-tight">
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Last event</div>
                        <div className="text-[12px] text-neutral-200 tabular-nums">
                          {relTime(r.last_event_at)}
                        </div>
                        {r.last_event_summary && (
                          <div className="text-[10.5px] text-neutral-500 truncate">
                            {r.last_event_summary}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 text-right leading-tight">
                        <StatusChip status={r.status} />
                        <div className="mt-1 text-[10.5px] text-neutral-500 tabular-nums">
                          Scored {relTime(r.last_scored_at)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Table — desktop / tablet only */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead className="bg-[#0b1426]/60">
                  <tr>
                    <SortHeader k="name">Supplier</SortHeader>
                    <SortHeader k="score">Grade</SortHeader>
                    <SortHeader k="score">Risk Score</SortHeader>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Industry</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Country</th>
                    <SortHeader k="recent">Last Event</SortHeader>
                    <SortHeader k="scanned">Last Scored</SortHeader>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {err && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-[13px] text-rose-300">
                        <div className="inline-flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          {err}
                        </div>
                      </td>
                    </tr>
                  )}

                  {!err && data.suppliers.map((r, i) => {
                    const delta = Number(r.last_score_delta || 0);
                    return (
                      <tr
                        key={r.id ?? r.domain}
                        onClick={() => navigate(`/supplier-security/${encodeURIComponent(r.domain)}`)}
                        className={`border-b border-sky-900/20 hover:bg-sky-900/15 cursor-pointer transition-colors ${i % 2 === 0 ? "bg-transparent" : "bg-[#091020]/40"}`}
                      >
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="h-7 w-7 shrink-0 rounded-md border border-sky-900/40 bg-[#0b1426] flex items-center justify-center overflow-hidden">
                              <DomainFavicon domain={r.domain} size={16} />
                            </div>
                            <div className="leading-tight min-w-0">
                              <div className="text-[13px] font-medium text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline truncate">
                                {r.display_name || r.domain}
                              </div>
                              <div className="text-[11px] text-neutral-500 truncate">{r.domain}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <GradePill grade={r.last_grade} />
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[13px] text-neutral-100 font-medium tabular-nums">
                              {displayRiskScore(r.last_score)}
                            </span>
                            {delta > 0 && (
                              <ArrowUpRight className="h-3.5 w-3.5 text-rose-400" />
                            )}
                            {delta < 0 && (
                              <ArrowDownRight className="h-3.5 w-3.5 text-emerald-400" />
                            )}
                          </div>
                          {delta !== 0 && (
                            <div
                              className={`text-[10.5px] tabular-nums mt-0.5 ${delta > 0 ? "text-rose-300" : "text-emerald-300"}`}
                            >
                              {delta > 0 ? "+" : ""}
                              {fmtScore(delta)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[12.5px] text-neutral-300">
                          {r.industry || "—"}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          {r.country ? (
                            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-neutral-300">
                              <span className="text-[16px] leading-none" title={r.country}>
                                {FLAG[r.country] || ""}
                              </span>
                              {r.country}
                            </span>
                          ) : (
                            <span className="text-[12px] text-neutral-600">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <div className="leading-tight">
                            <div className="text-[12.5px] text-neutral-200 tabular-nums">
                              {relTime(r.last_event_at)}
                            </div>
                            {r.last_event_summary && (
                              <div
                                className="text-[10.5px] text-neutral-500 truncate max-w-[260px]"
                                title={r.last_event_summary}
                              >
                                {r.last_event_summary}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[12.5px] text-neutral-400 tabular-nums">
                          {relTime(r.last_scored_at)}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <StatusChip status={r.status} />
                        </td>
                      </tr>
                    );
                  })}

                  {!err && !loading && data.suppliers.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-[13px] text-neutral-500">
                        {debouncedSearch
                          ? "No suppliers match your search."
                          : "No suppliers monitored yet. Add one from Manage Suppliers."}
                      </td>
                    </tr>
                  )}

                  {/* Filler rows keep the table height stable on short pages. */}
                  {!err && data.suppliers.length > 0 && data.suppliers.length < pageSize &&
                    Array.from({ length: pageSize - data.suppliers.length }).map((_, i) => (
                      <tr key={`filler-${i}`} aria-hidden className="pointer-events-none">
                        <td className="px-4 py-3 border-t border-sky-900/20" colSpan={8}>
                          <div className="h-8" />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Footer / pagination */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sky-900/30 px-4 py-2.5">
              <div className="text-[12px] text-neutral-500">
                Results:{" "}
                {data.total === 0
                  ? 0
                  : `${(page - 1) * pageSize + 1} – ${Math.min(page * pageSize, data.total)}`}{" "}
                of {data.total.toLocaleString()}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="inline-flex items-center justify-center rounded-md border border-sky-900/30 hover:bg-sky-900/30 p-1 text-neutral-400 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {pageWindow[0] > 1 && (
                  <span className="px-2 text-[12px] text-neutral-600">…</span>
                )}
                {pageWindow.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`min-w-[28px] h-7 rounded-md text-[12px] font-medium transition-colors ${
                      p === page
                        ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                        : "text-neutral-400 hover:text-sky-300 hover:bg-sky-900/30 border border-transparent"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                {pageWindow[pageWindow.length - 1] < totalPages && (
                  <span className="px-2 text-[12px] text-neutral-600">…</span>
                )}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="inline-flex items-center justify-center rounded-md border border-sky-900/30 hover:bg-sky-900/30 p-1 text-neutral-400 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-[12px] text-neutral-500">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-md border border-sky-900/30 bg-[#0b1426] px-2 py-1 text-[12px] text-neutral-200 outline-none focus:border-sky-500/50"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </section>
          )}
        </main>
      </div>
    </div>
  );
}
